import type { ReactNode, ComponentProps, FC } from "react";
import { useState, useRef, useEffect, useMemo } from "react";
import { Box, Skeleton } from "@mui/material";
import { PuckExtensionRenderer } from "./PuckExtensionRenderer";
import { useExtensions, ExtensionContext } from "src/api/client-api/puckExtensions/useExtensions";

type ExtensionPointProps = {
    /**
     * Identifiant unique de l'extension point
     */
    extensionPoint: string;

    /**
     * Contexte à passer aux extensions pour l'évaluation des conditions et l'accès aux données
     */
    context?: ExtensionContext;

    /**
     * Options d'affichage
     */
    showMetadata?: boolean;

    /**
     * Composant à afficher si aucune extension n'est trouvée
     */
    fallback?: ReactNode;

    /**
     * Composant de loading personnalisé
     */
    loadingComponent?: ReactNode;

    /**
     * Props supplémentaires pour le conteneur
     */
    containerProps?: ComponentProps<typeof Box>;

    /**
     * Désactiver le rendu des extensions
     */
    disabled?: boolean;

    /**
     * Priorité de l'extension point (pour optimiser les performances)
     */
    priority?: "high" | "normal" | "low";
};

/**
 * Composant principal pour rendre les extensions à un point donné
 */
export const ExtensionPoint: FC<ExtensionPointProps> = ({
    extensionPoint,
    context,
    showMetadata = false,
    fallback = null,
    loadingComponent,
    containerProps,
    disabled = false,
    priority = "normal",
}) => {
    // Options de cache selon la priorité
    const cacheOptions = useMemo(() => {
        switch (priority) {
            case "high":
                return { cacheTime: 10 * 60 * 1000, staleTime: 2 * 60 * 1000 };
            case "low":
                return { cacheTime: 2 * 60 * 1000, staleTime: 30 * 1000 };
            default:
                return { cacheTime: 5 * 60 * 1000, staleTime: 60 * 1000 };
        }
    }, [priority]);

    const { extensions, isLoading, isError, error } = useExtensions(extensionPoint, context, {
        enabled: !disabled,
        ...cacheOptions,
    });

    const runtimeExtensions = extensions ?? [];

    // Debug pour voir l'état des extensions (en mode dev seulement)
    // if (process.env.NODE_ENV === "development") {
    //     console.info(`ExtensionPoint "${extensionPoint}":`, {
    //         isLoading,
    //         isError,
    //         error,
    //         extensions,
    //         extensionCount: extensions?.length || 0,
    //     });
    // }

    // Affichage du loading
    if (isLoading) {
        if (loadingComponent) {
            return <>{loadingComponent}</>;
        }

        return (
            <Box {...containerProps}>
                <Skeleton variant="rectangular" height={60} animation="wave" />
            </Box>
        );
    }

    // Affichage en cas d'erreur (en mode développement seulement)
    if (isError && process.env.NODE_ENV === "development") {
        console.warn(`ExtensionPoint "${extensionPoint}" error:`, error);
    }

    // Aucune extension trouvée
    if (runtimeExtensions.length === 0) {
        return fallback ? <>{fallback}</> : null;
    }

    return (
        <Box data-extension-point={extensionPoint} data-extension-count={runtimeExtensions.length} {...containerProps}>
            {runtimeExtensions.map((extension) => (
                <PuckExtensionRenderer
                    key={`extension-${extension.id}`}
                    extension={extension}
                    context={context}
                    showMetadata={showMetadata}
                />
            ))}
        </Box>
    );
};

/**
 * Hook personnalisé pour créer un ExtensionPoint avec un contexte pré-configuré
 */
export const useExtensionPoint = (extensionPoint: string, baseContext?: ExtensionContext) => {
    return useMemo(() => {
        const ExtensionPointWithContext: FC<
            Omit<ExtensionPointProps, "extensionPoint" | "context"> & { context?: ExtensionContext }
        > = (props) => (
            <ExtensionPoint {...props} extensionPoint={extensionPoint} context={{ ...baseContext, ...props.context }} />
        );

        ExtensionPointWithContext.displayName = `ExtensionPoint(${extensionPoint})`;

        return ExtensionPointWithContext;
    }, [extensionPoint, baseContext]);
};

/**
 * Version optimisée pour des extension points conditionnels
 */
export const ConditionalExtensionPoint: FC<
    ExtensionPointProps & {
        condition: boolean;
    }
> = ({ condition, ...props }) => {
    if (!condition) {
        return props.fallback ? <>{props.fallback}</> : null;
    }

    return <ExtensionPoint {...props} />;
};

/**
 * Extension Point avec rendu différé (lazy)
 */
export const LazyExtensionPoint: FC<
    ExtensionPointProps & {
        intersectionOptions?: IntersectionObserverInit;
    }
> = ({ intersectionOptions, ...props }) => {
    const [isVisible, setIsVisible] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!ref.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            {
                threshold: 0.1,
                ...intersectionOptions,
            },
        );

        observer.observe(ref.current);

        return () => observer.disconnect();
    }, [intersectionOptions]);

    return (
        <Box ref={ref} {...props.containerProps}>
            {isVisible ?
                <ExtensionPoint {...props} />
            :   <Skeleton variant="rectangular" height={60} animation="wave" />}
        </Box>
    );
};
